"use client";

import { useState } from "react";
import { track } from "@/lib/analytics";
import { CheckResultView } from "@/components/tools/CheckResultView";
import { CheckProgress, ReviewingDots } from "@/components/tools/CheckProgress";
import {
  countFindings,
  htmlToText,
  runRamsCheck,
  DIMENSION_LABELS,
  type CheckResult,
} from "@/lib/ramsCheck";

/* In-builder rejection review. Sits by the PDF download controls: assembles
   the document exactly as the download does (the builder passes a callback that
   returns the same buildPdfHtml output, edits and triggers applied), turns it
   into plain text and runs it through /api/rams-check — the same review the
   standalone tool runs. The point of difference from the tool: gaps it finds
   can be fixed right here in the builder and the review re-run. */

export function BuilderReview({
  buildHtml,
  report,
  readiness,
}: {
  /** Returns the same HTML the PDF download builds (buildPdfHtml output). */
  buildHtml: () => string;
  /** Report slug — for analytics continuity with the rest of the builder. */
  report: string;
  /** Current readiness % — tagged onto the result event. */
  readiness: number;
}) {
  const [streaming, setStreaming] = useState(false);
  const [dimsArrived, setDimsArrived] = useState(0);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState("");

  async function review() {
    if (streaming) return;
    setError("");
    setResult(null);
    setDimsArrived(0);
    setStreaming(true);
    setOpen(true);
    track("builder_review_started", { report, readiness });

    try {
      const text = htmlToText(buildHtml());
      const parsed = await runRamsCheck(text, (json) => {
        const matches = json.match(/"key"/g);
        if (matches) setDimsArrived(Math.min(matches.length, DIMENSION_LABELS.length));
      });
      setResult(parsed);

      const counts = countFindings(parsed.dimensions);
      track("builder_review_result", {
        report,
        readiness,
        verdict: parsed.verdict,
        critical: counts.critical,
        major: counts.major,
        minor: counts.minor,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't reach the checker — check your connection and try again.");
    } finally {
      setStreaming(false);
    }
  }

  return (
    <div className="no-print mt-3 rounded-2xl border border-line bg-white p-5 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-[0.95rem] font-bold text-ink">Review like a PC would</p>
          <p className="mt-0.5 text-[0.78rem] leading-snug text-muted">
            Free, ~1 minute. Your document gets read the way a principal contractor&apos;s document controller
            reads it — scored on eight checks, with the gaps that trigger a bounce.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void review()}
          disabled={streaming}
          className="shrink-0 rounded-full bg-ink px-5 py-2.5 text-[0.82rem] font-semibold text-white transition-colors hover:bg-ink/85 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {streaming ? <>Reviewing<ReviewingDots /></> : result ? "Re-run review" : "Review like a PC would"}
        </button>
      </div>

      {streaming && (
        <div className="mt-4">
          <p className="text-[0.78rem] text-muted">
            Working through the eight checks — this usually takes under a minute.
          </p>
          <div className="mt-3">
            <CheckProgress dimsArrived={dimsArrived} />
          </div>
        </div>
      )}

      {error && !streaming && (
        <div role="alert" className="mt-4 rounded-xl border border-risk-high/40 bg-[#fef0e6] px-4 py-3 text-[#b34e10]">
          <div className="text-[0.85rem] font-bold">Couldn&apos;t complete the review</div>
          <p className="mt-1 text-[0.82rem] leading-relaxed">{error}</p>
          <button
            type="button"
            onClick={() => void review()}
            className="mt-2.5 rounded-full bg-ink px-4 py-2 text-[0.8rem] font-semibold text-white hover:bg-ink/85"
          >
            Try again
          </button>
        </div>
      )}

      {result && !streaming && (
        <div className="mt-4 border-t border-line pt-4">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex w-full items-center justify-between gap-2 text-left text-[0.82rem] font-semibold text-ink"
          >
            <span>Review result</span>
            <svg viewBox="0 0 24 24" className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          {open && (
            <div className="mt-3">
              <CheckResultView result={result} />
              <p className="mt-3 rounded-lg border border-emerald/30 bg-emerald-tint px-3 py-2 text-[0.8rem] leading-relaxed text-ink/85">
                Fix anything flagged above right here in the builder — edit the document, adjust the hazards or
                fill the missing fields — then run the review again to confirm it&apos;s clear before you download.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
